import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatSnackBar} from '@angular/material';
import {PackageService} from '../../../../services/package.service';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {FuseTranslationLoaderService} from '../../../../../@fuse/services/translation-loader.service';
import {locale as polish} from '../../../../translations/pl';
import {locale as english} from '../../../../translations/en';
import {PackageType} from '../../../../shared/models/package.model';
import {ConfirmModalComponent} from '../../../shared/components/confirm-modal/confirm-modal.component';
import {AuthService} from '../../../../services/auth.service';
import {User} from '../../../../shared/models/users.model';
import {parseForm} from '../../../../shared/utils/parse-form';
import {ValidateGreaterThanZero} from '../../../../shared/validators/greaterThanZero.validator';
import {markFormGroupTouched} from "../../../../shared/utils/markFormGroupAsTouched";

@Component({
  selector: 'app-package-types-editor',
  templateUrl: './package-types-editor.component.html',
  styleUrls: ['./package-types-editor.component.scss']
})
export class PackageTypesEditorComponent implements OnInit {
  public packageTypeForm: FormGroup;
  public packageTypeUuid: PackageType['uuid'];
  public packageType;
  public mode: string;
  public isLoading = false;
  public user: User;

  constructor(public packageTypeEditorDialogRef: MatDialogRef<PackageTypesEditorComponent>,
              @Inject(MAT_DIALOG_DATA) private data: any,
              private matDialog: MatDialog,
              private packageService: PackageService,
              private translateService: TranslateService,
              private snackBar: MatSnackBar,
              private formBuilder: FormBuilder,
              private _fuseTranslationLoaderService: FuseTranslationLoaderService,
              private authService: AuthService) {
    this.mode = data.mode;
    this.packageTypeUuid = data.packageTypeUuid;

    this._fuseTranslationLoaderService.loadTranslations(polish, english);
  }

  ngOnInit() {
    this.createForm({});
    this.user = this.authService.getUser();

    if (this.mode === 'edit' || this.mode === 'view') {
      this.packageService.getPackage(this.packageTypeUuid).subscribe(
        res => {
          this.packageType = res;
          this.createForm(this.packageType);
        }
      );
    }
  }

  public submitPackageType() {
    this.isLoading = true;
    markFormGroupTouched(this.packageTypeForm);
    if (this.packageTypeForm.invalid) {
      this.isLoading = false;
      return;
    }
    if (this.mode === 'add') {
      this.packageService.createPackageType(parseForm(this.packageTypeForm.value)).subscribe(
        () => {
          this.packageTypeEditorDialogRef.close();

          this.packageTypeEditorDialogRef.afterClosed().subscribe(() => {
            this.packageService.onEditEvent.emit();
          });

          this.snackBar.open(this.translateService.instant('PACKAGE.MESSAGES.CREATE_SUCCESS'), '', {duration: 5000});
        }, () => {
          this.isLoading = false;
          this.snackBar.open(this.translateService.instant('PACKAGE.MESSAGES.CREATE_ERROR'), '', {duration: 5000});
        }
      )
    } else {
      this.packageService.updatePackageType(this.packageTypeUuid, parseForm(this.packageTypeForm.value)).subscribe(
        () => {
          this.packageTypeEditorDialogRef.close();

          this.packageTypeEditorDialogRef.afterClosed().subscribe(() => {
            this.packageService.onEditEvent.emit();
          });

          this.snackBar.open(this.translateService.instant('PACKAGE.MESSAGES.UPDATE_SUCCESS'), '', {duration: 5000});
        }, () => {
          this.isLoading = false;
          this.snackBar.open(this.translateService.instant('PACKAGE.MESSAGES.UPDATE_ERROR'), '', {duration: 5000});
        }
      )
    }
  }

  public deletePackageType() {
    let deleteDialogRef = this.matDialog.open(ConfirmModalComponent, {
      disableClose: false,
      autoFocus: false
    });

    deleteDialogRef.componentInstance.confirmMessage = this.translateService.instant('PACKAGE.MESSAGES.DELETE_QUESTION');
    deleteDialogRef.componentInstance.titleMessage = this.translateService.instant('PACKAGE.MESSAGES.DELETE_PACKAGE_TITLE');
    deleteDialogRef.componentInstance.confirmButton = this.translateService.instant('PACKAGE.DELETE');

    deleteDialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.packageService.deletePackageType(this.packageTypeUuid).subscribe(
          () => {
            this.packageTypeEditorDialogRef.close();
            this.packageTypeEditorDialogRef.afterClosed().subscribe(() => {
              this.packageService.onEditEvent.emit();
            });
            this.snackBar.open(this.translateService.instant('PACKAGE.MESSAGES.DELETE_SUCCESS'), '', {duration: 5000});
          }, () => {
            this.snackBar.open(this.translateService.instant('PACKAGE.MESSAGES.DELETE_ERROR'), '', {duration: 5000});
          }
        )
      }
    });
  }

  public enableEditMode() {
    this.packageTypeEditorDialogRef.close();
    this.matDialog.open(PackageTypesEditorComponent, {
      panelClass: 'package-editor-dialog',
      autoFocus: false,
      disableClose: true,
      data: {
        mode: 'edit',
        packageTypeUuid: this.packageTypeUuid
      }
    });
  }

  public getTitle(): string {
    switch (this.mode) {
      case 'add':
        return this.translateService.instant('PACKAGE.TITLE_ADD');
      case 'edit':
        return this.translateService.instant('PACKAGE.TITLE_EDIT');
      case 'view':
        return this.translateService.instant('PACKAGE.TITLE_VIEW');
      default:
        return;
    }
  }

  private createForm(inputData: PackageType | any) {
    this.packageTypeForm = this.formBuilder.group({
      name: new FormControl(inputData.name || '', Validators.required),
      description: new FormControl(inputData.description || ''),
      length: new FormControl(inputData.length || null,
        [Validators.pattern('[\\d]+[.]{0,1}[\\d]{0,3}'), ValidateGreaterThanZero]),
      width: new FormControl(inputData.width || null,
        [Validators.pattern('[\\d]+[.]{0,1}[\\d]{0,3}'), ValidateGreaterThanZero]),
      height: new FormControl(inputData.height || null,
        [Validators.pattern('[\\d]+[.]{0,1}[\\d]{0,3}'), ValidateGreaterThanZero]),
      volume: new FormControl(inputData.volume || null,
        [Validators.pattern('[\\d]+[.]{0,1}[\\d]{0,3}'), ValidateGreaterThanZero]),
      capacity: new FormControl(inputData.capacity || null,
        [Validators.pattern('[\\d]+[.]{0,1}[\\d]{0,3}'), ValidateGreaterThanZero]),
      unit: new FormControl(this.mode === 'add' ? 'pieces' : inputData.unit),
      isPublic: new FormControl(this.mode === 'add' ? true : inputData.isPublic),
      active: new FormControl(this.mode === 'add' ? true : inputData.active),
    });

    if (this.mode === 'view') {
      this.packageTypeForm.disable();
    }
  }
}
